<template>
  <div class="movie-block">
    <div class="movie-block__poster">
      <div class="movie-poster">
        <div
          class="movie-poster__image"
          :data-link="movie.slug && !isOnDetailPage ? filmDetailUrl : null"
        >
          <div class="ratio ratio-poster loading-state">
            <img loading="lazy" :src="movieImage" :alt="movie.filmTitle || ''" />
          </div>
          <img class="blurred-img" loading="lazy" :src="movieImage" :alt="movie.filmTitle || ''" />
        </div>
        <div class="movie-poster__play" v-if="hasTrailer">
          <button
            class="play-button"
            title="Fenster öffnen und Trailer abspielen"
            @click="handlePlayButtonClick"
          />
        </div>
      </div>
    </div>

    <div class="movie-block__content">
      <div v-if="movie.slug">
        <h2 class="h3" :data-link="!isOnDetailPage ? filmDetailUrl : null">
          <span v-html="movie.filmTitle"></span>
        </h2>
      </div>
      <div v-else>
        <h2 class="h3">
          <span v-html="movie.filmTitle"></span>
        </h2>
      </div>

      <h3 class="h4" v-if="movie.subTitle" v-html="movie.subTitle"></h3>
      <p v-if="movie.teaser" v-html="movie.teaser"></p>

      <div class="movie-attributes">
        <ul>
          <li v-if="movie.genre">
            <ui-icon icon="film"></ui-icon>
            <span v-html="movie.genre"></span>
          </li>
          <li v-if="movie.length_hours !== undefined && movie.length_minutes !== undefined">
            <ui-icon icon="alarm"></ui-icon>
            <span v-if="movie.length_hours > 0">{{ movie.length_hours }}h </span
            >{{ movie.length_minutes }}m
          </li>
          <li v-if="startDateDisplay">{{ startDateDisplay }}</li>
        </ul>
      </div>

      <div class="fsk-holder">
        <div class="fsk-holder__top">
          <ui-icon
            v-if="movie.fsk"
            :icon="`fsk-badge-${movie.fsk}`"
            size="auto"
            :title="`FSK ${movie.fsk}`"
            viewBox="0 0 500 156"
          ></ui-icon>
          <ui-icon
            v-else
            icon="fsk-badge-unknown"
            size="auto"
            title="FSK (noch) unbekannt"
            viewBox="0 0 500 156"
          ></ui-icon>
          <span v-if="movie.fsk === '12'">*</span>
          <div v-if="movie.fsk && parseInt(movie.fsk) > 0">
            <details v-if="movie.descriptor">
              <summary>Deskriptoren</summary>
              <span>{{ movie.descriptor }}</span>
            </details>
            <span v-else>Deskriptoren (noch) unbekannt</span>
          </div>
        </div>
        <div v-if="movie.fsk === '12'" class="parental-guidance" :title="getParentalGuidance()">
          * {{ getParentalGuidance() }}
        </div>
      </div>

      <!-- Show filmtext if no performances and filmtext available, otherwise show shortText -->
      <div v-if="!hasPerformances && movie.filmtext" v-html="movie.filmtext"></div>
      <p v-else-if="movie.shortText" class="short-text" v-html="movie.shortText"></p>

      <div v-if="!isOnDetailPage">
        <a :href="filmDetailUrl" class="btn btn-outline">
          <span>Mehr erfahren</span>
          <ui-icon icon="arrow-20"></ui-icon>
        </a>
      </div>
    </div>

    <div class="movie-block__schedules">
      <div class="schedules">
        <template v-if="hasPerformances">
          <!-- Calendar View -->
          <CalendarView
            v-if="viewType === 'calendar'"
            :performanceGroups="movie.performances"
            :movie="movie"
          />
          <!-- Table View -->
          <TableView
            v-if="viewType === 'table'"
            :performanceGroups="movie.performances"
            :movie="movie"
          />
        </template>
        <div v-else class="schedules-bg-layer text-center py-4">
          <div class="h4">Keine Termine verfügbar</div>
          <p class="text-muted">Für diesen Film sind aktuell keine Vorstellungen geplant.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted } from "vue";
import CalendarView from "./CalendarView.vue";
import TableView from "./TableView.vue";
import { useImageHelpers } from "../composables/useImageHelpers.js";
import { useUrlHelpers } from "../composables/useUrlHelpers.js";
import { useScheduleStore } from "../stores/scheduleStore.js";
import { PARENTAL_GUIDANCE_TEXT } from "../composables/constants.js";
import { useDateHelpers } from "../composables/useDateHelpers.js";

const { formatDate } = useDateHelpers();

const props = defineProps({
  movie: {
    type: Object,
    required: true,
    validator: (value) => {
      return value && typeof value === "object" && value.filmTitle;
    },
  },
});

const { getSolarScheduleImage } = useImageHelpers();
const { getFilmDetailUrl } = useUrlHelpers();
const scheduleStore = useScheduleStore();

// Use store state for viewType (Pinia stores are reactive)
const viewType = computed(() => scheduleStore.scheduleView);

// Check if we should show startDate (same logic as Blade template)
const showStartDate = computed(() => {
  const routeName = window.appRoute || "";
  const validRoutes = ["vorschau", "preview", "content"];
  return validRoutes.some((route) => routeName.includes(route));
});

const handleViewChange = (event) => {
  if (event.detail && event.detail.viewType) {
    scheduleStore.setScheduleView(event.detail.viewType);
  }
};

onMounted(() => {
  window.addEventListener("schedule-view-changed", handleViewChange);
});

onUnmounted(() => {
  window.removeEventListener("schedule-view-changed", handleViewChange);
});

// Event handlers
const handlePlayButtonClick = () => {
  const event = new CustomEvent("trailerplayer-play", {
    detail: props.movie,
    bubbles: true,
  });
  document.dispatchEvent(event);
};

// Computed properties for better performance
const movieImage = computed(() => getSolarScheduleImage(props.movie, "330"));
const filmDetailUrl = computed(() => getFilmDetailUrl(props.movie.slug));

// Check if we're currently on the detail page for this movie
const isOnDetailPage = computed(() => {
  if (!props.movie.slug) return false;
  const currentPath = window.location.pathname;
  const detailPath = filmDetailUrl.value;
  // Check if current path matches detail path (handles both /film/slug and /programm/film/slug)
  return currentPath === detailPath || currentPath === `/programm/film/${props.movie.slug}`;
});

const hasTrailer = computed(() => props.movie.trailerAvailable && props.movie.slug);
const hasPerformances = computed(
  () => props.movie.performances && props.movie.performances.length > 0,
);
const firstPerformance = computed(() => {
  const groups = Array.isArray(props.movie.performances) ? props.movie.performances : [];
  return groups
    .flatMap((group) => Object.values(group?.performances ?? {}))
    .sort((a, b) => (a.unixdatetime ?? 0) - (b.unixdatetime ?? 0))[0];
});

// Computed for start date display
const startDateDisplay = computed(() => {
  // Only show on specific routes (vorschau, preview, content)
  if (!showStartDate.value) return null;

  // 1. CMS user override start date
  const filmstart = props.movie.filmstart;
  // 2. Bundesstartdatum
  const startDate = props.movie.startDate;
  // 3. Date of first performance from today onwards (including today)
  const firstDate = firstPerformance.value?.date;

  const date = filmstart || startDate || firstDate || null;
  const prefix = props.movie.filmstarttext ?? null;

  if (!date) return null;

  const stringArr = [formatDate(date)];

  if (prefix) {
    stringArr.unshift(prefix);
  }

  return stringArr.join(" ");
});

const getParentalGuidance = () => PARENTAL_GUIDANCE_TEXT;
</script>
